import { User, Users } from 'lucide-react';
import type { cycleProgress } from '../stats';

export type DashboardView = 'mine' | 'team';

/** "My work / Team" segmented switch */
export function ViewToggle({
  value,
  onChange,
  mineDisabled,
}: Readonly<{
  value: DashboardView;
  onChange: (v: DashboardView) => void;
  mineDisabled?: boolean;
}>) {
  const item = (v: DashboardView, label: string, Icon: typeof User) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      disabled={v === 'mine' && mineDisabled}
      aria-pressed={value === v}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        value === v
          ? 'bg-modern-charcoal text-white shadow-sm'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
  return (
    <div
      role="group"
      aria-label="Whose accounts to show"
      className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm"
    >
      {item('mine', 'My work', User)}
      {item('team', 'Team', Users)}
    </div>
  );
}

/** Thin bar: where today sits in the selected workflow month */
export function CycleBar({
  progress,
  monthLabel,
}: Readonly<{
  progress: ReturnType<typeof cycleProgress>;
  monthLabel: string;
}>) {
  const pct = Math.round((progress.day / progress.days) * 100);
  const text =
    progress.state === 'current'
      ? `Day ${progress.day} of ${progress.days} in the ${monthLabel} workflow · ${progress.days - progress.day} days left`
      : progress.state === 'past'
        ? `${monthLabel} workflow month has ended`
        : `${monthLabel} workflow month hasn't started`;
  return (
    <div className="mt-3 max-w-xl">
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-label="Workflow month progress"
        aria-valuenow={progress.day}
        aria-valuemin={0}
        aria-valuemax={progress.days}
      >
        <div
          className={`h-full rounded-full ${progress.state === 'past' ? 'bg-slate-400' : 'bg-modern-charcoal'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-slate-500">{text}</p>
    </div>
  );
}
